<script lang="ts">
  import { onMount, untrack } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    createWorkOrder,
    fetchNextWorkOrderNumber,
  } from "../../../../../../../../../../../../../../api/kehrblatt/work-orders"
  import WorkOrderFormFields from "../../../../../../../../../../../../_shared/_modules/work-order-form-fields"
  import type { WorkOrderFormValue } from "../../../../../../../../../../../../_shared/_logic/work-order-form-types"
  import {
    normalizeDateOnly,
    parseOptionalDurationMinutes,
    parseOptionalNumber,
  } from "../../../../../../../../../../../../_shared/_logic/work-order-form-utils"
  import { buildCustomOrderType } from "../../../../../../../../../../../../../../_shared/_logic/order-type-labels"
  import { orderTypeSelectionPayload } from "../../../../../../../../../../../../_shared/_logic/order-type-selection"
  import type { WorkOrderAssignmentRow, WorkOrderAssignmentSelection } from "../../contract"
  import { buildWorkOrderSelectionFromCreated } from "./_logic/workOrderSelection"

  let {
    organizationId,
    row,
    orderTypes = [],
    onCreated,
    onremoveOverlay = null,
  }: {
    organizationId: string
    row: WorkOrderAssignmentRow
    orderTypes?: OrderType[]
    onCreated: (selection: WorkOrderAssignmentSelection) => void
    onremoveOverlay?: (() => void) | null
  } = $props()

  const stringValue = (value: unknown) => (typeof value === "string" ? value.trim() : "")
  const numberString = (value: unknown) => {
    if (typeof value === "number" && Number.isFinite(value)) return String(value)

    if (typeof value === "string" && value.trim()) return value.trim()

    return ""
  }

  const initialForm = untrack(
    (): WorkOrderFormValue => ({
      property_id: row.propertyId ?? "",
      property_unit_id: row.propertyUnitId ?? "",
      order_number: "",
      order_type_ids: [],
      custom_order_type: buildCustomOrderType(
        stringValue(row.payload.order_type_label ?? row.payload.order_type),
      ),
      due_date: stringValue(row.payload.due_date),
      priority: "",
      estimated_duration_min: numberString(row.payload.estimated_duration_min),
      allowed_employee_ids: [],
    }),
  )

  let form = $state<WorkOrderFormValue>(initialForm)
  let formValid = $state(false)
  let submitting = $state(false)

  onMount(() => {
    void fetchNextWorkOrderNumber(organizationId)
      .then((response) => {
        const nextNumber = response?.next_order_number?.trim()

        if (nextNumber && !form.order_number.trim()) {
          form = { ...form, order_number: nextNumber }
        }
      })
      .catch((err) => {
        console.error("work_order_assignment_next_number_load_failed", err)
      })
  })

  const submit = async (data: WorkOrderFormValue) => {
    if (!row.propertyId) {
      newNotification({
        class: "error",
        html: "Liegenschaft fehlt. Auftrag kann nicht angelegt werden.",
      })

      return
    }

    submitting = true
    try {
      const created = await createWorkOrder({
        property_id: row.propertyId,
        property_unit_id: row.propertyUnitId || null,
        property_unit_label: row.propertyUnitLabel || row.unitLabel || null,
        order_number: data.order_number.trim() || null,
        ...orderTypeSelectionPayload(data),
        due_date: normalizeDateOnly(data.due_date),
        priority: parseOptionalNumber(data.priority),
        estimated_duration_min: parseOptionalDurationMinutes(data.estimated_duration_min),
        allowed_employee_ids: data.allowed_employee_ids?.length ? data.allowed_employee_ids : null,
      })
      const selection = buildWorkOrderSelectionFromCreated({ workOrder: created, row })
      onCreated(selection)
      newNotification({ class: "success", html: "Auftrag angelegt." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("work_order_assignment_create_work_order_failed", err)
      newNotification({ class: "error", html: "Auftrag konnte nicht angelegt werden." })
    } finally {
      submitting = false
    }
  }
</script>

<WorkOrderFormFields
  {organizationId}
  bind:value={form}
  bind:valid={formValid}
  bind:submitting
  {orderTypes}
  showPropertySelect={false}
  showAllowedEmployees={false}
  showReset={false}
  submitLabel="Auftrag anlegen"
  mode="create"
  theme="dark"
  brightBackground={true}
  onSubmit={submit}
/>
